import { EMPTY_GUID } from '../../../constants';
import { OcAuthResponse, OcChatConfig } from '../../../types/oc';
import { Messages, MessageStatus } from '../../Message/types/messages';
import {
  ApiMessagesResponse,
  ApiPrivacySettingsResponse,
  ChatHistoryItem,
  CrawlerJob,
  DiscussionPost,
  DiscussionPostDraft,
  DraftDiscussionRequest,
  OcCommunity,
  OcPrivacySettings,
  OcSiteMessagesResponse,
  SubmitDiscussionRequest,
  SubmitDiscussionResponse,
} from '../types/apiTypes';
import {
  transformApiMessagesResponse,
  transformApiPrivacySettingsResponse,
  transformOcSiteMessagesResponse,
} from './apiTransformers';
import {
  createCrestApiClient,
  createOcApiClient,
  get,
  isOcSiteTransport,
  post,
} from './ocApiClient';

/**
 * OC Endpoints - Business logic for OC API operations
 * Uses the generic HTTP client to make specific API calls
 */

/**
 * Builds the agent-identity portion of an ocSite chat request body.
 *
 * `aiAssistantKey` is preferred: oc-api resolves the recipe code, search filters, prompt addition
 * and confidence settings from the assistant row itself, so the agent's configuration survives and
 * the caller cannot name a recipe the tenant is not configured for. `recipeCode` is only sent when
 * the host has no assistant key to give us, which oc-api still accepts for the entry points whose
 * code comes from tenant configuration rather than an assistant row.
 */
const ocSiteAgentBody = (config: OcChatConfig): Record<string, string> => {
  const aiAssistantKey = config.aiAssistantKey?.trim();
  if (aiAssistantKey && aiAssistantKey !== EMPTY_GUID) {
    return { aiAssistantKey };
  }

  const recipeCode = config.recipeCode?.trim();
  if (!recipeCode) {
    throw new Error(
      'aiAssistantKey or recipeCode is required when apiTransport is ocSite',
    );
  }
  return { recipeCode };
};

/**
 * Check if user is authenticated
 * Uses the Contacts WhoAmI Crest endpoint (member cookie / IAM auth).
 * Always calls Crest, even when the widget chat transport is ocSite.
 */
export const checkOcAuth = async (
  config: OcChatConfig,
): Promise<OcAuthResponse> => {
  const client = createCrestApiClient(config);

  try {
    const url = `${client.baseUrl}/contacts/getwhoami`;

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: client.headers,
    });

    // Handle authentication errors (401/403) - not logged in
    if (response.status === 401 || response.status === 403) {
      return { isAuthenticated: false };
    }

    // Handle server/network errors (500, etc.) - throw to show error state
    if (!response.ok) {
      throw new Error(`Authentication check failed: ${response.status} ${response.statusText}`);
    }

    // Parse response - this endpoint returns Contact object directly, not wrapped
    const data = await response.json();

    // If we got a ContactKey back, user is authenticated
    if (data && data.ContactKey && data.ContactKey !== EMPTY_GUID) {
      return {
        isAuthenticated: true,
        userId: data.ContactKey,
      };
    }

    return { isAuthenticated: false };
  } catch (error) {
    // Re-throw errors to show error state in UI (network issues, server errors, etc.)
    throw error instanceof Error ? error : new Error(String(error));
  }
};

/**
 * Initialize a new chat session
 */
export const initChatSession = async (
  config: OcChatConfig,
): Promise<Messages> => {
  const client = createOcApiClient(config);

  if (isOcSiteTransport(config)) {
    const data = await post<OcSiteMessagesResponse>(client, '/AiAssistant/CreateSession', {
      ...ocSiteAgentBody(config),
      originToolId: config.originToolId || config.aiAssistantKey,
    });
    return transformOcSiteMessagesResponse(data);
  }

  const data = await post<ApiMessagesResponse>(
    client,
    `/aiassistant/CreateSession?aiAssistantKey=${config.aiAssistantKey}&originToolId=${config.originToolId || config.aiAssistantKey}`,
  );

  return transformApiMessagesResponse(data);
};

/**
 * Fetch privacy settings. Crest uses GET (PascalCase); oc-api uses POST (camelCase in data).
 */
export const getPrivacySettings = async (
  config: OcChatConfig,
): Promise<OcPrivacySettings> => {
  const client = createOcApiClient(config);

  if (isOcSiteTransport(config)) {
    const data = await post<OcPrivacySettings>(
      client,
      '/AiAssistant/GetPrivacySettings',
      {},
    );
    return {
      isPrivate: data?.isPrivate,
      isAnonymous: data?.isAnonymous,
      allowPublicAccess: data?.allowPublicAccess,
    };
  }

  const data = await get<ApiPrivacySettingsResponse>(client, '/aiassistant/GetPrivacySettings');
  return transformApiPrivacySettingsResponse(data);
};

/**
 * Resume an existing chat session
 */
export const resumeChatSession = async (
  config: OcChatConfig,
  sessionId: string,
): Promise<Messages> => {
  const client = createOcApiClient(config);

  if (isOcSiteTransport(config)) {
    const data = await post<OcSiteMessagesResponse>(client, '/AiAssistant/ResumeSession', {
      ...ocSiteAgentBody(config),
      sessionKey: sessionId,
    });
    return transformOcSiteMessagesResponse(data);
  }

  const data = await post<ApiMessagesResponse>(
    client,
    `/aiassistant/ResumeSession?aiAssistantKey=${config.aiAssistantKey}&sessionKey=${sessionId}`,
  );

  return transformApiMessagesResponse(data);
};

/**
 * Send a message in a chat session
 */
export const sendChatMessage = async (
  config: OcChatConfig,
  message: string,
  sessionId: string,
  overrides?: Record<string, string>,
): Promise<Messages> => {
  if (!message || !sessionId) {
    throw new Error('Message and sessionId are required');
  }

  const client = createOcApiClient(config);

  if (isOcSiteTransport(config)) {
    const body: Record<string, unknown> = {
      ...ocSiteAgentBody(config),
      sessionKey: sessionId,
      message,
    };
    if (overrides && Object.keys(overrides).length > 0) {
      body.overrides = overrides;
    }

    const data = await post<OcSiteMessagesResponse>(client, '/AiAssistant/SendMessage', body);
    return transformOcSiteMessagesResponse(data);
  }

  const body: Record<string, unknown> = {
    AIAssistantKey: config.aiAssistantKey,
    SessionKey: sessionId,
    Message: message,
  };
  if (overrides && Object.keys(overrides).length > 0) {
    body.Overrides = overrides;
  }

  const data = await post<ApiMessagesResponse>(
    client,
    '/aiassistant/SendMessage',
    body,
  );

  return transformApiMessagesResponse(data);
};

/**
 * Send a message reaction (like/dislike)
 */
export const sendMessageReaction = async (
  config: OcChatConfig,
  messageId: string,
  status: MessageStatus,
  sessionId: string,
): Promise<boolean> => {
  if (!messageId || !sessionId) {
    throw new Error('MessageId and sessionId are required');
  }

  const client = createOcApiClient(config);

  try {
    // One body serves both hosts: oc-api binds loose action parameters through MVC's
    // case-insensitive value provider, and Crest's PascalCase model binds through Newtonsoft's
    // case-insensitive fallback. Don't split these back apart on casing alone.
    await post(client, '/AiAssistant/MessageReaction', {
      sessionKey: sessionId,
      messageKey: messageId,
      likeStatus: status,
    });
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Track message copy event
 */
export const trackMessageCopy = async (
  config: OcChatConfig,
  messageId: string,
  sessionId: string,
): Promise<boolean> => {
  if (!messageId || !sessionId) {
    throw new Error('MessageId and sessionId are required');
  }

  const client = createOcApiClient(config);

  try {
    await post(client, '/AiAssistant/MessageCopied', {
      sessionKey: sessionId,
      messageKey: messageId,
    });
    return true;
  } catch (error) {
    return false;
  }
};


/**
 * Get communities where user can contribute discussions
 * Calls GetCommunitiesCanContribute endpoint to fetch communities where the user can post discussions/messages
 */
export const getCommunitiesCanContribute = async (
  config: OcChatConfig,
): Promise<OcCommunity[]> => {
  const client = createCrestApiClient(config);

  const data = await get<OcCommunity[]>(
    client,
    '/communities/GetCommunitiesCanContribute',
  );

  return data || [];
};

/**
 * Draft a discussion post using AI
 * Calls Draft endpoint with recipe code and session ID to get AI-generated content
 */
export const draftDiscussionPost = async (
  config: OcChatConfig,
  request: DraftDiscussionRequest,
): Promise<DiscussionPostDraft> => {
  const client = createCrestApiClient(config);

  const data = await post<DiscussionPostDraft>(
    client,
    '/aiAssistant/Draft',
    request,
  );

  return data;
};

/**
 * Submit a discussion post to a community
 * Calls Enterprise API PostDiscussion endpoint with discussion details
 * Returns the full created discussion post object including URL
 */
export const submitDiscussionPost = async (
  config: OcChatConfig,
  request: SubmitDiscussionRequest,
): Promise<SubmitDiscussionResponse> => {
  const client = createCrestApiClient(config);

  try {
    const response = await post<DiscussionPost>(
      client,
      '/discussions/PostDiscussionSimple',
      {
        CommunityKey: request.communityKey,
        Subject: request.subject,
        Message: request.body,
        TopicTagKeys: request.topicTagKeys || [],
      },
    );

    // API returns the full DiscussionPost object with LinkToMessage as the URL
    return {
      success: true,
      discussionPost: response,
      discussionUrl: response.LinkToMessage,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export const fetchAiSearchResultData = async (
  config: OcChatConfig,
  searchPrompt: string): Promise<Messages> => {
  const client = createCrestApiClient(config);

  const data = await post<ApiMessagesResponse>(
    client,
    '/aiassistant/Search',
    {
      searchPrompt: searchPrompt,
      aiAssistantKey: config.aiAssistantKey,
    },
  );

  return transformApiMessagesResponse(data);
};

/**
 * Get chat history for the user
 * Calls GetChatHistory endpoint to fetch user's chat sessions
 */
export const getChatHistory = async (
  config: OcChatConfig,
  originToolId: string,
): Promise<ChatHistoryItem[]> => {
  // History always comes from Crest, which resolves the caller from HLAuthToken -- a stateless
  // 24-hour token that can outlive the OC session. On the OC site the host page is authoritative
  // for identity, so a visitor it reports as anonymous must never reach this endpoint, or their
  // predecessor's session titles come back. Callers are expected to skip this entirely for
  // anonymous visitors; this is the backstop that makes the leak impossible rather than avoided.
  if (isOcSiteTransport(config) && config.hostAuth?.isAuthenticated === false) {
    return [];
  }

  const client = createCrestApiClient(config);

  const data = await get<ChatHistoryItem[]>(
    client,
    `/aiassistant/GetChatHistory?originToolId=${originToolId}`,
  );

  return data || [];
};

/**
 * Get crawler job information for the tenant for external source display
 */
export const getCrawlerJobs = async (
  config: OcChatConfig,
): Promise<CrawlerJob[]> => {
  const client = createOcApiClient(config);

  if (isOcSiteTransport(config)) {
    const data = await get<Array<{ crawlerJobKey?: string; crawlerName?: string }>>(
      client,
      '/AiCrawler/GetCrawlerJobs',
    );
    return (data || []).map((job) => ({
      CrawlerJobKey: job.crawlerJobKey || '',
      CrawlerName: job.crawlerName || '',
    }));
  }

  const data = await get<CrawlerJob[]>(
    client,
    `/aiassistant/GetCrawlerJobs`,
  );

  return data || [];
};

/**
 * Send user feedback on an AI message
 */
export const sendFeedback = async (
  config: OcChatConfig,
  sessionId: string,
  messageId: string,
  feedback: string,
): Promise<boolean> => {
  if (!sessionId || !messageId || !feedback) {
    throw new Error('SessionId, messageId, and feedback are required');
  }

  const client = createOcApiClient(config);

  try {
    // Shared body, as in sendMessageReaction: both hosts bind it case-insensitively.
    await post(client, '/AiAssistant/SendFeedback', {
      sessionKey: sessionId,
      messageKey: messageId,
      feedback: feedback.trim(),
    });
    return true;
  } catch (error) {
    return false;
  }
};
